import {
	DISCOUNT_TYPE_VALUE,
	resolveDiscountTypeValue,
	isAutoPromoDiscountType,
	isFixedDiscount,
} from 'helpers/discountTypeValues'
import { v4 as uuidv4 } from 'uuid'

export function getDiscountsForPos(reduxDiscounts, posId) {
	if (!posId || !reduxDiscounts?.by_pos) {
		return { days: [], products: [], fixed: [] }
	}
	const key = String(posId)
	const bucket = reduxDiscounts.by_pos[key] || reduxDiscounts.by_pos[posId]
	if (!bucket) return { days: [], products: [], fixed: [] }
	return {
		days: bucket.days || [],
		products: bucket.products || [],
		fixed: bucket.fixed || [],
	}
}

function normalizeTime(raw) {
	if (raw == null || raw === '') return '00:00:00'
	const s = String(raw)
	const parts = s.split(':')
	const hh = parts[0] || '00'
	const mm = parts[1] || '00'
	const ss = parts[2] || '00'
	return `${String(hh).padStart(2, '0')}:${String(mm).padStart(2, '0')}:${String(ss).padStart(2, '0')}`
}

export function isDiscountDayActive(dayRule, currentTime = new Date()) {
	const startNorm = normalizeTime(dayRule.start_time)
	const endNorm = normalizeTime(dayRule.end_time)
	if (startNorm === '00:00:00' && endNorm === '00:00:00') return true

	const startParts = startNorm.split(':')
	const endParts = endNorm.split(':')
	const startTime = new Date(currentTime)
	const endTime = new Date(currentTime)
	startTime.setHours(parseInt(startParts[0], 10), parseInt(startParts[1], 10), parseInt(startParts[2] || 0, 10), 0)
	endTime.setHours(parseInt(endParts[0], 10), parseInt(endParts[1], 10), parseInt(endParts[2] || 0, 10), 0)
	return currentTime >= startTime && currentTime <= endTime
}

export function matchesDiscountOrderType(dayRule, orderTypeId, orderTypeSubId = null) {
	const subIds = (dayRule?.order_type_sub_ids || []).map(Number).filter((id) => id > 0)
	if (subIds.length > 0 && orderTypeSubId != null && orderTypeSubId !== '') {
		return subIds.includes(Number(orderTypeSubId))
	}

	const ot = Number(orderTypeId)
	const isOn = (v) => v === true || v === 1 || v === '1' || v === 't' || v === 'true'
	return (
		(ot === 1 && isOn(dayRule.discount_hall)) ||
		(ot === 2 && isOn(dayRule.discount_takeaway)) ||
		(ot === 3 && isOn(dayRule.discount_delivery))
	)
}

function upsertProductDiscount(dataCopy, product, dayRule, discountToProduct) {
	const typeValue = resolveDiscountTypeValue(dayRule)
	const discountProductIndex = dataCopy.discounts.findIndex((item) => (
		item.status === 0 &&
		Number(item.product_id) === Number(product.product_id) &&
		resolveDiscountTypeValue(item) === typeValue &&
		Number(item.discount_service_id) === Number(dayRule.discount_service_id)
	))

	if (discountProductIndex >= 0) {
		dataCopy.discounts[discountProductIndex].quantity = product.quantity
		dataCopy.discounts[discountProductIndex].discount = Number(discountToProduct)
		dataCopy.discounts[discountProductIndex].discount_payment_value = Number(discountToProduct)
		dataCopy.discounts[discountProductIndex].total_discount = Number(discountToProduct * product.quantity)
		return
	}

	dataCopy.discounts.push({
		status: 0,
		quantity: product.quantity,
		product_id: product.product_id,
		discount_service_id: dayRule.discount_service_id,
		discount_type: dayRule.discount_type,
		discount_type_value: typeValue,
		discount_payment_type: dayRule.discount_payment_type,
		discount_payment_value: Number(discountToProduct),
		discount: Number(discountToProduct),
		total_discount: Number(discountToProduct * product.quantity),
		affects_total: true,
	})
}

/**
 * @param {object} dataCopy — cheque
 * @param {object} discountTypes — { days, products }
 * @param {object} options
 * @param {number} options.orderTypeId — 2 takeaway / 3 delivery
 * @param {number} [options.orderTypeSubId] — exact order_types_subs.id
 * @param {array} options.catalogProducts — for gift product lookup
 * @param {number|null} options.clientCountCheques — прошлых чеков клиента (0 — первый заказ)
 */
export function returnDiscount(dataCopy, discountTypes, options = {}) {
	const orderTypeId = options.orderTypeId
	const orderTypeSubId = options.orderTypeSubId
	const catalogProducts = options.catalogProducts || []
	const clientCountCheques = options.clientCountCheques
	const currentTime = new Date()
	let dayOfWeek = currentTime.getDay()
	if (dayOfWeek === 0) dayOfWeek = 7

	if (!Array.isArray(dataCopy.discounts)) dataCopy.discounts = []
	if (!Array.isArray(dataCopy.products)) dataCopy.products = []

	const keptProducts = []
	for (let p = 0; p < dataCopy.products.length; p++) {
		const product = dataCopy.products[p]
		if (product.is_promo_gift) {
			const giftTotal = Number(product.total_price || product.sale_price || 0)
			dataCopy.total_amount = Number(dataCopy.total_amount || 0) - giftTotal
			dataCopy.total_amount_service = Number(dataCopy.total_amount_service || 0) - giftTotal
			continue
		}
		keptProducts.push(product)
	}
	dataCopy.products = keptProducts
	// Keep fixed; remove auto dish/cheque/promo that we rebuild
	dataCopy.discounts = dataCopy.discounts.filter((discount) => {
		if (isFixedDiscount(discount)) return true
		if (isAutoPromoDiscountType(discount)) return false
		const v = resolveDiscountTypeValue(discount)
		if (v === DISCOUNT_TYPE_VALUE.AUTO_DISH || v === DISCOUNT_TYPE_VALUE.AUTO_CHEQUE) return false
		if (v === DISCOUNT_TYPE_VALUE.FIRST_ORDER) return false
		return true
	})

	let ruleFound = false
	let firstOrderRuleFound = false
	let menuRuleFound = false
	let buyGetRuleFound = false
	let giftRuleFound = false

	const days = discountTypes?.days || []
	for (let i = 0; i < days.length; i++) {
		const dayRule = days[i]
		if (!dayRule[`day${dayOfWeek}`]) continue
		if (!matchesDiscountOrderType(dayRule, orderTypeId, orderTypeSubId)) continue
		if (!isDiscountDayActive(dayRule, currentTime)) continue

		const discountTypeValue = resolveDiscountTypeValue(dayRule)

		if (discountTypeValue === DISCOUNT_TYPE_VALUE.AUTO_CHEQUE) {
			const chequeBaseAmount = Number(dataCopy.total_amount) || 0
			// Порог не задан — правило действует с любой суммы заказа
			const minChequeAmount = Number(dayRule.cheque_amount) || 0
			const DT3Index = dataCopy.discounts.findIndex(
				(discount) => resolveDiscountTypeValue(discount) === DISCOUNT_TYPE_VALUE.AUTO_CHEQUE
			)

			if (chequeBaseAmount > 0 && minChequeAmount <= chequeBaseAmount) {
				const ruleValue = Number(dayRule.discount_payment_value) || 0
				let discountAmount = 0
				if (Number(dayRule.discount_payment_type) === 1) discountAmount = ruleValue
				if (Number(dayRule.discount_payment_type) === 2) discountAmount = chequeBaseAmount * ruleValue / 100

				// В discount_payment_value — значение правила (процент или сумма), а не готовая сумма:
				// по нему считают и sumChequeDiscounts, и бэк — иначе процент берётся от процента.
				const chequeDiscountPatch = {
					cheque_amount: dayRule.cheque_amount,
					discount_type: dayRule.discount_type,
					discount_type_value: discountTypeValue,
					discount_service_id: dayRule.discount_service_id,
					discount_payment_type: Number(dayRule.discount_payment_type),
					discount_payment_value: ruleValue,
					discount: discountAmount,
					total_discount: discountAmount,
				}

				if (DT3Index === -1) {
					dataCopy.discounts.push({ status: 0, ...chequeDiscountPatch })
				} else {
					dataCopy.discounts[DT3Index] = { ...dataCopy.discounts[DT3Index], ...chequeDiscountPatch }
				}
			} else if (DT3Index >= 0) {
				dataCopy.discounts.splice(DT3Index, 1)
			}
		}

		// first_order: скидка на первый заказ клиента
		if (discountTypeValue === DISCOUNT_TYPE_VALUE.FIRST_ORDER && !firstOrderRuleFound) {
			const firstOrderBase = Number(dataCopy.total_amount) || 0
			const FOIndex = dataCopy.discounts.findIndex(
				(discount) => resolveDiscountTypeValue(discount) === DISCOUNT_TYPE_VALUE.FIRST_ORDER
			)
			const isFirstOrder = clientCountCheques !== null && clientCountCheques !== undefined && clientCountCheques !== ''
				&& Number(clientCountCheques) === 0

			if (isFirstOrder && firstOrderBase > 0) {
				firstOrderRuleFound = true
				const ruleValue = Number(dayRule.discount_payment_value) || 0
				let discountAmount = 0
				if (Number(dayRule.discount_payment_type) === 1) discountAmount = ruleValue
				if (Number(dayRule.discount_payment_type) === 2) discountAmount = firstOrderBase * ruleValue / 100

				const firstOrderPatch = {
					discount_type: dayRule.discount_type,
					discount_type_value: discountTypeValue,
					discount_service_id: dayRule.discount_service_id,
					discount_payment_type: Number(dayRule.discount_payment_type),
					discount_payment_value: ruleValue,
					discount: discountAmount,
					total_discount: discountAmount,
				}

				if (FOIndex === -1) {
					dataCopy.discounts.push({ status: 0, ...firstOrderPatch })
				} else {
					dataCopy.discounts[FOIndex] = { ...dataCopy.discounts[FOIndex], ...firstOrderPatch }
				}
			} else if (FOIndex >= 0) {
				dataCopy.discounts.splice(FOIndex, 1)
			}
		}

		if (discountTypeValue === DISCOUNT_TYPE_VALUE.AUTO_DISH && !ruleFound) {
			ruleFound = true
			for (let j = 0; j < dataCopy.products.length; j++) {
				const line = dataCopy.products[j]
				const discountProductIndex = dataCopy.discounts.findIndex((item) => (
					item.status === 0 &&
					Number(item.product_id) === Number(line.product_id) &&
					resolveDiscountTypeValue(item) === DISCOUNT_TYPE_VALUE.AUTO_DISH
				))

				if (discountProductIndex >= 0) {
					dataCopy.discounts[discountProductIndex].quantity = line.quantity
					dataCopy.discounts[discountProductIndex].discount = Number(dataCopy.discounts[discountProductIndex].discount_payment_value)
					dataCopy.discounts[discountProductIndex].total_discount =
						Number(line.quantity * dataCopy.discounts[discountProductIndex].discount_payment_value)
				}

				if (Number(line.status) === 0 && discountProductIndex === -1) {
					const discountProduct = (discountTypes.products || []).find((item) => (
						Number(item.product_id) === Number(line.product_id) &&
						Number(item.discount_service_id) === Number(dayRule.discount_service_id)
					))
					if (discountProduct?.product_id) {
						let discountToProduct = 0
						if (Number(discountProduct.discount_payment_type) === 1) {
							discountToProduct = Number(discountProduct.discount_payment_value)
						}
						if (Number(discountProduct.discount_payment_type) === 2) {
							discountToProduct = Number(line.sale_price) * (Number(discountProduct.discount_payment_value) / 100)
						}
						dataCopy.discounts.push({
							status: 0,
							quantity: line.quantity,
							product_id: discountProduct.product_id,
							discount_service_id: discountProduct.discount_service_id,
							discount_type: discountProduct.discount_type,
							discount_type_value: resolveDiscountTypeValue(discountProduct),
							discount_payment_type: discountProduct.discount_payment_type,
							discount_payment_value: discountProduct.discount_payment_value,
							discount: Number(discountToProduct),
							total_discount: Number(discountToProduct * line.quantity),
						})
					}
				}
			}
		}

		if (discountTypeValue === DISCOUNT_TYPE_VALUE.MENU_PROMO && !menuRuleFound) {
			const minCheque = Number(dayRule.cheque_amount || 0)
			if (minCheque > 0 && Number(dataCopy.total_amount) < minCheque) {
				continue
			}
			menuRuleFound = true
			const menuScope = Number(dayRule.menu_scope || 1)
			// Фиксированная сумма действует на заказ целиком, процент — на каждую позицию
			const isFixedSum = Number(dayRule.discount_payment_type) === 1
			let fixedSumBase = 0

			for (let j = 0; j < dataCopy.products.length; j++) {
				const product = dataCopy.products[j]
				if (Number(product.status) !== 0 || product.is_promo_gift) continue
				const matched = menuScope === 1 || (discountTypes.products || []).some((item) => (
					Number(item.product_id) === Number(product.product_id) &&
					Number(item.discount_service_id) === Number(dayRule.discount_service_id)
				))
				if (!matched) continue

				if (isFixedSum) {
					fixedSumBase += Number(product.total_price) || 0
					continue
				}

				const discountToProduct = Number(product.sale_price) * (Number(dayRule.discount_payment_value) / 100)
				upsertProductDiscount(dataCopy, product, dayRule, discountToProduct)
			}

			if (isFixedSum && fixedSumBase > 0) {
				// Скидка не может быть больше суммы позиций, на которые она действует
				const fixedSumAmount = Math.min(Number(dayRule.discount_payment_value) || 0, fixedSumBase)
				dataCopy.discounts.push({
					status: 0,
					cheque_amount: dayRule.cheque_amount,
					discount_type: dayRule.discount_type,
					discount_type_value: discountTypeValue,
					discount_service_id: dayRule.discount_service_id,
					discount_payment_type: 1,
					discount_payment_value: fixedSumAmount,
					discount: fixedSumAmount,
					total_discount: fixedSumAmount,
					affects_total: true,
				})
			}
		}

		if (discountTypeValue === DISCOUNT_TYPE_VALUE.BUY_GET && !buyGetRuleFound) {
			buyGetRuleFound = true
			const buyQty = Math.max(1, Number(dayRule.buy_quantity || 1))
			const getQty = Math.max(1, Number(dayRule.get_quantity || 1))
			const getLimit = Math.max(0, Number(dayRule.get_limit_per_cheque || 0))
			// Лимит общий на весь чек, а не на каждый товар акции
			let giftsLeft = getLimit
			const handledProductIds = new Set()

			for (let j = 0; j < dataCopy.products.length; j++) {
				const product = dataCopy.products[j]
				if (Number(product.status) !== 0 || product.is_promo_gift) continue
				const productId = Number(product.product_id)
				if (handledProductIds.has(productId)) continue

				const matched = (discountTypes.products || []).some((item) => (
					Number(item.product_id) === productId &&
					Number(item.discount_service_id) === Number(dayRule.discount_service_id)
				))
				if (!matched) continue
				handledProductIds.add(productId)

				const paidQty = dataCopy.products.reduce((sum, item) => {
					if (
						Number(item.status) === 0 &&
						!item.is_promo_gift &&
						Number(item.product_id) === productId
					) {
						return sum + Number(item.quantity || 0)
					}
					return sum
				}, 0)

				// Лимит подарков на чек: пусто — акция множится кратно (6 купил — 6 в подарок),
				// N — не больше N подарков в чеке, как у агрегаторов при 1+1
				let freeQty = Math.floor(paidQty / buyQty) * getQty
				if (getLimit > 0) freeQty = Math.min(freeQty, giftsLeft)
				if (freeQty <= 0) continue

				const salePrice = Number(product.sale_price || product.price || 0)
				const giftLine = {
					...product,
					quantity: freeQty,
					status: 0,
					sale_price: salePrice,
					price: salePrice,
					total_price: salePrice * freeQty,
					selected: false,
					is_promo_gift: true,
					promo_gift_kind: DISCOUNT_TYPE_VALUE.BUY_GET,
					promo_discount_service_id: dayRule.discount_service_id,
					comment: '',
					transaction_id: uuidv4(),
				}
				if (getLimit > 0) giftsLeft -= freeQty
				dataCopy.products.push(giftLine)
				dataCopy.total_amount = Number(dataCopy.total_amount || 0) + (salePrice * freeQty)
				dataCopy.total_amount_service = Number(dataCopy.total_amount_service || 0) + (salePrice * freeQty)
				upsertProductDiscount(dataCopy, giftLine, {
					...dayRule,
					discount_payment_type: 1,
					discount_payment_value: salePrice,
				}, salePrice)
			}
		}

		if (discountTypeValue === DISCOUNT_TYPE_VALUE.GIFT && !giftRuleFound) {
			const minCheque = Number(dayRule.cheque_amount || 0)
			const giftProductId = Number(dayRule.gift_product_id || 0)
			if (!giftProductId) continue
			if (minCheque > 0 && Number(dataCopy.total_amount) < minCheque) continue

			const triggerProducts = (discountTypes.products || []).filter((item) => (
				Number(item.discount_service_id) === Number(dayRule.discount_service_id)
			))
			if (triggerProducts.length > 0) {
				const hasTrigger = dataCopy.products.some((product) => (
					Number(product.status) === 0 &&
					!product.is_promo_gift &&
					triggerProducts.some((trigger) => Number(trigger.product_id) === Number(product.product_id))
				))
				if (!hasTrigger) continue
			}

			const catalogProduct = catalogProducts.find((item) => (
				Number(item.product_id) === giftProductId || Number(item.id) === giftProductId
			))
			if (!catalogProduct) continue

			giftRuleFound = true
			const salePrice = Number(catalogProduct.sale_price || catalogProduct.price || 0)
			const giftLine = {
				...catalogProduct,
				product_id: giftProductId,
				quantity: 1,
				status: 0,
				sale_price: salePrice,
				price: salePrice,
				total_price: salePrice,
				selected: false,
				is_promo_gift: true,
				promo_discount_service_id: dayRule.discount_service_id,
				comment: '',
				transaction_id: uuidv4(),
			}
			dataCopy.products.push(giftLine)
			dataCopy.total_amount = Number(dataCopy.total_amount || 0) + salePrice
			dataCopy.total_amount_service = Number(dataCopy.total_amount_service || 0) + salePrice
			upsertProductDiscount(dataCopy, giftLine, dayRule, salePrice)
		}
	}

	return dataCopy
}

export function sumChequeDiscounts(dataCopy) {
	let discount = 0
	for (let i = 0; i < (dataCopy.discounts || []).length; i++) {
		const row = dataCopy.discounts[i]
		if (
			row.affects_total === false ||
			row.affects_total === 0 ||
			row.affects_total === '0' ||
			row.affects_total === 'f' ||
			row.affects_total === 'false'
		) {
			continue
		}
		if (row.product_id) {
			discount += Number(row.quantity) * Number(row.discount)
		} else {
			if (Number(row.discount_payment_type) === 1) {
				discount += Number(row.discount_payment_value)
			}
			if (Number(row.discount_payment_type) === 2) {
				discount += Number(dataCopy.total_amount_service) * Number(row.discount_payment_value) / 100
			}
		}
	}
	return discount
}

export { DISCOUNT_TYPE_VALUE, resolveDiscountTypeValue, isAutoPromoDiscountType, isFixedDiscount }
